import { useMemo, useState } from 'react'
import { Button, RadioGroup, ScrollShadow } from '@heroui/react'
import { useTranslation } from 'react-i18next'
import { usePostHog } from '@posthog/react'
import clsx from 'clsx'
import { useTheme } from '@/ui/theme'
import type { ReturnsProductDetailsResponse } from '@itsrever/rever-api'
import { QuantitySelector } from '@/ui/components/quantity-selector'
import { ProductImages } from './product-images'
import { ProductVariantButton } from './product-variant-button'
import { useAppSelector } from '@/redux/hooks'
import { useCart } from '@/hooks/use-cart'
import { ProductDescription } from './product-description'
import { hasVariantStock } from '../helpers/has-variant-stock'
import { ProductPrice } from './product-price'
import { ProductExtraDetails } from './product-extra-details'
import { getGridPosition } from '../helpers/get-grid-position'

interface ProductDetailsProps {
  product: ReturnsProductDetailsResponse
  returnedItemId: string
  onClose: () => void
}

export const ProductDetails = ({
  product: { product_info, signed_product_raw },
  returnedItemId,
  onClose
}: ProductDetailsProps) => {
  const { t } = useTranslation()

  const posthog = usePostHog()

  const checkoutId = useAppSelector(
    (store) => store.returnsApi.order.response?.checkout_id
  )

  const theme = useTheme()

  const { addItemToCart } = useCart()

  const [activeImage, setActiveImage] = useState<string | undefined>(
    product_info?.featured_image_url
  )
  const [{ quantity, variant }, setSelected] = useState({
    variant: product_info?.variants?.[0],
    quantity: 1
  })

  const maxQuantity =
    variant && hasVariantStock(variant) ? variant.inventory_quantity : 1

  const images = useMemo(() => {
    const productVariants = product_info?.variants ?? []
    const productImages = product_info?.image_urls ?? []
    const variantsImages = productVariants.map(
      (variant) => variant.image_urls?.[0]
    )

    return Array.from(new Set([...productImages, ...variantsImages]))
  }, [product_info?.image_urls, product_info?.variants])

  const onVariantSelect = (variantId: string) => {
    const variant = product_info?.variants?.find(
      (variant) => variant.platform_id === variantId
    )

    setSelected({ variant, quantity: 1 })

    if (variant?.image_urls?.[0]) {
      setActiveImage(variant?.image_urls?.[0])
    }
  }

  const addToCart = () => {
    const productId = product_info?.platform_id

    if (productId && variant) {
      const gridPosition = getGridPosition(productId)

      posthog.capture('FC - Product added to cart', {
        row: gridPosition?.row,
        col: gridPosition?.col,
        product: product_info,
        checkout_id: checkoutId
      })

      if (variant) {
        addItemToCart(returnedItemId, quantity, variant, {
          product_info,
          signed_product_raw
        })
      }

      onClose()
    }
  }

  const color = theme.colors.primary.main
  const textColor = theme.colors.primary.contrastText
  const rounded = theme.borderRadius

  return (
    <div className="mx-auto flex size-full flex-col gap-4 overflow-auto overscroll-none px-3 pt-9 md:flex-row md:gap-8 md:py-9 2xl:max-w-6xl">
      <ProductImages
        images={images}
        selectedImage={activeImage}
        onSelectImage={setActiveImage}
      />
      <div className="flex min-h-0 flex-1 flex-col gap-6">
        <div className="space-y-4">
          <div className="space-y-2">
            <h1 className="text-2xl font-normal">{product_info?.name}</h1>
            {variant ? (
              <ProductPrice
                variant={variant}
                productId={product_info?.platform_id}
                collectionIds={product_info?.collection_ids}
              />
            ) : null}
          </div>
          <div className="space-y-3">
            {product_info?.description ? (
              <div>
                <ProductDescription description={product_info.description} />
              </div>
            ) : null}
            {product_info?.details ? (
              <ProductExtraDetails details={product_info?.details} />
            ) : null}
          </div>
        </div>
        <div className="flex flex-col gap-4 md:min-h-0">
          <p id="variant-options">{t('full_catalog.select_option')}</p>
          <ScrollShadow className="md:h-full md:overflow-y-auto">
            <RadioGroup
              aria-labelledby="variant-options"
              onValueChange={onVariantSelect}
              value={variant?.platform_id}
              orientation="vertical"
            >
              {product_info?.variants?.map((variant) => (
                <ProductVariantButton
                  value={variant.platform_id ?? ''}
                  key={variant.platform_id}
                  variant={variant}
                />
              ))}
            </RadioGroup>
          </ScrollShadow>
        </div>
        <div className="sticky bottom-0 mt-auto flex items-center justify-center gap-6 bg-white py-4 md:relative md:pb-0">
          <QuantitySelector
            value={quantity}
            onValueChange={(q) =>
              setSelected((prev) => ({
                ...prev,
                quantity: q
              }))
            }
            minValue={1}
            maxValue={maxQuantity}
          />
          <Button
            id="confirm-variant-button"
            variant={color === 'transparent' ? 'light' : 'solid'}
            radius="sm"
            size={color === 'transparent' ? 'md' : 'lg'}
            fullWidth
            disabled={variant ? !hasVariantStock(variant) : true}
            data-testid="continue"
            className={clsx('disabled:opacity-60', {
              'underline underline-offset-2 hover:!bg-neutral-100':
                color === 'transparent'
            })}
            style={{
              width: '100%',
              maxWidth: '20rem',
              backgroundColor: color === 'transparent' ? 'transparent' : color,
              color: textColor,
              borderRadius: rounded
            }}
            onPress={addToCart}
          >
            {t('full_catalog.add_to_cart')}
          </Button>
        </div>
      </div>
    </div>
  )
}
